import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  type Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { CollectionItem, PropertyCollection } from '@/types/models'

export const FAVOURITES_ID = 'favourites'
export const FAVOURITES_NAME = 'Favourites'
export const NOTE_MAX = 500
export const NAME_MAX = 60
export const DESCRIPTION_MAX = 280

function asDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate()
  }
  return new Date()
}

export function normalizeCollectionName(name: string): string {
  return name.trim().replace(/\s+/g, ' ')
}

export function collectionNameKey(name: string): string {
  return normalizeCollectionName(name).toLowerCase()
}

export function parsePropertyCollection(
  id: string,
  data: Record<string, unknown>,
): PropertyCollection {
  return {
    id,
    name: String(data.name ?? ''),
    nameLower: String(data.nameLower ?? ''),
    description: String(data.description ?? ''),
    isFavourites: Boolean(data.isFavourites),
    itemCount: typeof data.itemCount === 'number' ? data.itemCount : 0,
    updatedAt: asDate(data.updatedAt),
    createdAt: asDate(data.createdAt),
  }
}

export function parseCollectionItem(
  id: string,
  data: Record<string, unknown>,
): CollectionItem {
  return {
    propertyId: String(data.propertyId ?? id),
    fullAddress: String(data.fullAddress ?? ''),
    note: String(data.note ?? ''),
    addedAt: asDate(data.addedAt),
    updatedAt: asDate(data.updatedAt),
  }
}

export async function ensureFavouritesCollection(uid: string): Promise<void> {
  const colRef = doc(db, 'users', uid, 'collections', FAVOURITES_ID)
  const existing = await getDoc(colRef)
  if (existing.exists()) return

  const nameKey = collectionNameKey(FAVOURITES_NAME)
  const batch = writeBatch(db)
  const now = serverTimestamp()
  batch.set(colRef, {
    name: FAVOURITES_NAME,
    nameLower: nameKey,
    description: '',
    isFavourites: true,
    itemCount: 0,
    updatedAt: now,
    createdAt: now,
  })
  batch.set(doc(db, 'users', uid, 'collectionNames', nameKey), {
    collectionId: FAVOURITES_ID,
  })
  await batch.commit()
}

export async function isPropertyInFavourites(
  uid: string,
  propertyId: string,
): Promise<boolean> {
  const snap = await getDoc(
    doc(db, 'users', uid, 'collections', FAVOURITES_ID, 'items', propertyId),
  )
  return snap.exists()
}

export async function toggleFavourite(
  uid: string,
  propertyId: string,
  fullAddress: string,
): Promise<{ favourited: boolean }> {
  await ensureFavouritesCollection(uid)
  const itemRef = doc(db, 'users', uid, 'collections', FAVOURITES_ID, 'items', propertyId)
  const colRef = doc(db, 'users', uid, 'collections', FAVOURITES_ID)
  const existing = await getDoc(itemRef)

  if (existing.exists()) {
    const batch = writeBatch(db)
    batch.delete(itemRef)
    batch.update(colRef, {
      itemCount: increment(-1),
      updatedAt: serverTimestamp(),
    })
    await batch.commit()
    return { favourited: false }
  }

  const batch = writeBatch(db)
  const now = serverTimestamp()
  batch.set(itemRef, {
    propertyId,
    fullAddress,
    note: '',
    addedAt: now,
    updatedAt: now,
  })
  batch.update(colRef, {
    itemCount: increment(1),
    updatedAt: now,
  })
  await batch.commit()
  return { favourited: true }
}

export async function listCollections(uid: string): Promise<PropertyCollection[]> {
  const q = query(
    collection(db, 'users', uid, 'collections'),
    orderBy('updatedAt', 'desc'),
  )
  const snap = await getDocs(q)
  const cols = snap.docs.map((d) =>
    parsePropertyCollection(d.id, d.data() as Record<string, unknown>),
  )
  cols.sort((a, b) => {
    if (a.isFavourites && !b.isFavourites) return -1
    if (!a.isFavourites && b.isFavourites) return 1
    return b.updatedAt.getTime() - a.updatedAt.getTime()
  })
  return cols
}

export async function getCollection(
  uid: string,
  collectionId: string,
): Promise<PropertyCollection | null> {
  const snap = await getDoc(doc(db, 'users', uid, 'collections', collectionId))
  if (!snap.exists()) return null
  return parsePropertyCollection(snap.id, snap.data() as Record<string, unknown>)
}

export async function listCollectionItems(
  uid: string,
  collectionId: string,
): Promise<CollectionItem[]> {
  const q = query(
    collection(db, 'users', uid, 'collections', collectionId, 'items'),
    orderBy('addedAt', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => parseCollectionItem(d.id, d.data() as Record<string, unknown>))
}

export async function createCollection(
  uid: string,
  input: { name: string; description?: string },
): Promise<PropertyCollection> {
  const name = normalizeCollectionName(input.name)
  if (!name) throw new Error('Collection name is required.')
  if (name.length > NAME_MAX) throw new Error(`Name must be ${NAME_MAX} characters or fewer.`)
  const description = (input.description ?? '').trim().slice(0, DESCRIPTION_MAX)
  const nameKey = collectionNameKey(name)
  if (nameKey === collectionNameKey(FAVOURITES_NAME)) {
    throw new Error('That name is reserved for Favourites.')
  }

  const nameRef = doc(db, 'users', uid, 'collectionNames', nameKey)
  const nameSnap = await getDoc(nameRef)
  if (nameSnap.exists()) {
    throw new Error('You already have a collection with that name.')
  }

  const colRef = doc(collection(db, 'users', uid, 'collections'))
  const now = serverTimestamp()
  const batch = writeBatch(db)
  batch.set(colRef, {
    name,
    nameLower: nameKey,
    description,
    isFavourites: false,
    itemCount: 0,
    updatedAt: now,
    createdAt: now,
  })
  batch.set(nameRef, { collectionId: colRef.id })
  await batch.commit()

  return {
    id: colRef.id,
    name,
    nameLower: nameKey,
    description,
    isFavourites: false,
    itemCount: 0,
    updatedAt: new Date(),
    createdAt: new Date(),
  }
}

export async function addPropertyToCollection(
  uid: string,
  collectionId: string,
  propertyId: string,
  fullAddress: string,
  note = '',
): Promise<void> {
  if (collectionId === FAVOURITES_ID) {
    await ensureFavouritesCollection(uid)
  }
  const itemRef = doc(db, 'users', uid, 'collections', collectionId, 'items', propertyId)
  const colRef = doc(db, 'users', uid, 'collections', collectionId)
  const existing = await getDoc(itemRef)
  if (existing.exists()) {
    if (note !== undefined) {
      await setDoc(
        itemRef,
        {
          note: note.slice(0, NOTE_MAX),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      )
    }
    return
  }

  const batch = writeBatch(db)
  const now = serverTimestamp()
  batch.set(itemRef, {
    propertyId,
    fullAddress,
    note: note.slice(0, NOTE_MAX),
    addedAt: now,
    updatedAt: now,
  })
  batch.update(colRef, {
    itemCount: increment(1),
    updatedAt: now,
  })
  await batch.commit()
}

export async function removePropertyFromCollection(
  uid: string,
  collectionId: string,
  propertyId: string,
): Promise<void> {
  const itemRef = doc(db, 'users', uid, 'collections', collectionId, 'items', propertyId)
  const colRef = doc(db, 'users', uid, 'collections', collectionId)
  const existing = await getDoc(itemRef)
  if (!existing.exists()) return

  const batch = writeBatch(db)
  batch.delete(itemRef)
  batch.update(colRef, {
    itemCount: increment(-1),
    updatedAt: serverTimestamp(),
  })
  await batch.commit()
}

export async function updateCollectionItemNote(
  uid: string,
  collectionId: string,
  propertyId: string,
  note: string,
): Promise<void> {
  await setDoc(
    doc(db, 'users', uid, 'collections', collectionId, 'items', propertyId),
    {
      note: note.slice(0, NOTE_MAX),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

export async function renameCollection(
  uid: string,
  collectionId: string,
  input: { name: string; description?: string },
): Promise<void> {
  if (collectionId === FAVOURITES_ID) {
    throw new Error('Favourites can’t be renamed.')
  }
  const name = normalizeCollectionName(input.name)
  if (!name) throw new Error('Collection name is required.')
  if (name.length > NAME_MAX) throw new Error(`Name must be ${NAME_MAX} characters or fewer.`)
  const description =
    input.description !== undefined
      ? input.description.trim().slice(0, DESCRIPTION_MAX)
      : undefined
  const nameKey = collectionNameKey(name)
  if (nameKey === collectionNameKey(FAVOURITES_NAME)) {
    throw new Error('That name is reserved for Favourites.')
  }

  const colRef = doc(db, 'users', uid, 'collections', collectionId)
  const colSnap = await getDoc(colRef)
  if (!colSnap.exists()) throw new Error('Collection not found.')
  const current = parsePropertyCollection(colSnap.id, colSnap.data() as Record<string, unknown>)

  const batch = writeBatch(db)
  if (nameKey !== current.nameLower) {
    const newNameRef = doc(db, 'users', uid, 'collectionNames', nameKey)
    const taken = await getDoc(newNameRef)
    if (taken.exists()) {
      throw new Error('You already have a collection with that name.')
    }
    batch.delete(doc(db, 'users', uid, 'collectionNames', current.nameLower))
    batch.set(newNameRef, { collectionId })
  }

  batch.update(colRef, {
    name,
    nameLower: nameKey,
    ...(description !== undefined ? { description } : {}),
    updatedAt: serverTimestamp(),
  })
  await batch.commit()
}

export async function deleteCollection(uid: string, collectionId: string): Promise<void> {
  if (collectionId === FAVOURITES_ID) {
    throw new Error('Favourites can’t be deleted.')
  }
  const colRef = doc(db, 'users', uid, 'collections', collectionId)
  const colSnap = await getDoc(colRef)
  if (!colSnap.exists()) return
  const current = parsePropertyCollection(colSnap.id, colSnap.data() as Record<string, unknown>)

  const itemsSnap = await getDocs(
    collection(db, 'users', uid, 'collections', collectionId, 'items'),
  )
  const batch = writeBatch(db)
  itemsSnap.docs.forEach((d) => batch.delete(d.ref))
  batch.delete(doc(db, 'users', uid, 'collectionNames', current.nameLower))
  batch.delete(colRef)
  await batch.commit()
}

export async function createCollectionAndAddProperty(
  uid: string,
  input: { name: string; description?: string },
  propertyId: string,
  fullAddress: string,
): Promise<PropertyCollection> {
  const name = normalizeCollectionName(input.name)
  if (!name) throw new Error('Collection name is required.')
  if (name.length > NAME_MAX) throw new Error(`Name must be ${NAME_MAX} characters or fewer.`)
  const description = (input.description ?? '').trim().slice(0, DESCRIPTION_MAX)
  const nameKey = collectionNameKey(name)
  if (nameKey === collectionNameKey(FAVOURITES_NAME)) {
    throw new Error('That name is reserved for Favourites.')
  }

  const nameRef = doc(db, 'users', uid, 'collectionNames', nameKey)
  const nameSnap = await getDoc(nameRef)
  if (nameSnap.exists()) {
    throw new Error('You already have a collection with that name.')
  }

  const colRef = doc(collection(db, 'users', uid, 'collections'))
  const itemRef = doc(db, 'users', uid, 'collections', colRef.id, 'items', propertyId)
  const now = serverTimestamp()
  const batch = writeBatch(db)
  batch.set(colRef, {
    name,
    nameLower: nameKey,
    description,
    isFavourites: false,
    itemCount: 1,
    updatedAt: now,
    createdAt: now,
  })
  batch.set(nameRef, { collectionId: colRef.id })
  batch.set(itemRef, {
    propertyId,
    fullAddress,
    note: '',
    addedAt: now,
    updatedAt: now,
  })
  await batch.commit()

  return {
    id: colRef.id,
    name,
    nameLower: nameKey,
    description,
    isFavourites: false,
    itemCount: 1,
    updatedAt: new Date(),
    createdAt: new Date(),
  }
}
